import React from 'react';
import { 
  Drawer, 
  List, 
  ListItem, 
  ListItemIcon, 
  ListItemText, 
  Box, 
  Tooltip, 
  Divider, 
  Typography, 
  useMediaQuery, 
  useTheme, 
  IconButton,
  CircularProgress
} from '@mui/material';
import { 
  Home, 
  Server, 
  Users, 
  Settings, 
  Lock, 
  HelpCircle, 
  X, 
  Calendar, 
  MessageCircle,
  FileText,
  FolderOpen,
  Database,
  Files,
  Brain,
  Mic,
  Send,
  ShoppingCart,
} from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from '../../i18n';

const Sidebar = ({ mobileOpen, handleDrawerToggle, isOpen, handleSidebarOpen, darkMode }) => {
  const { t } = useTranslation('layout');
  const { user, loading, appAccess } = useAuth();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const currentYear = new Date().getFullYear();

  const menuItems = [
    { textKey: 'sidebar.dashboard', icon: Home, path: '/', app: "Required" },
    { textKey: 'sidebar.calendar', icon: Calendar, path: '/booking', app: "booking" },
    { textKey: 'sidebar.clients', icon: Users, path: '/clients', app: "crm_app" },
    { textKey: 'sidebar.chats', icon: MessageCircle, path: '/chats', app: "ai_app" },
    { textKey: 'sidebar.trainingMessages', icon: Brain, path: '/training-messages', app: "ai_app", superAdminOnly: true },
    { textKey: 'sidebar.voiceAgent', icon: Mic, path: '/voice-agent', app: "voice_agent" },
    { textKey: 'sidebar.outboundCampaigns', icon: Send, path: '/outbound', app: "outbound_app" },
    { textKey: 'sidebar.carStock', icon: Server, path: '/car_stock', app: "car_stock" },
    { textKey: 'sidebar.autoscoutMarket', icon: ShoppingCart, path: '/autoscout-market', app: "autoscout_market" },
    { textKey: 'sidebar.documents', icon: Files, path: '/documents', app: "documents" },
    { textKey: 'sidebar.promptRules', icon: FileText, path: '/prompt-rules', app: "documents" },

    { textKey: 'sidebar.settings', icon: Settings, path: '/aziende', app: "settings", altApp: "ai_schedule" }, // Also visible with ai_schedule permission
    { textKey: 'sidebar.users', icon: Users, path: '/utenti', app: "user_management", alwaysShow: true }, // Always visible for self-service
    { textKey: 'sidebar.roles', icon: Lock, path: '/ruoli', app: "user_management" },
    { textKey: 'sidebar.userRegistrationForm', icon: Server, path: '/forms', app: "settings" },
  ];

  const filteredMenuItems = loading
    ? []
    : menuItems.filter(item => {
        // Always show items marked with alwaysShow (like "Utenti" for self-service)
        if (item.alwaysShow) return true;
        
        if (item.app === "Required") return true;
        if (user && user.role === "superadmin") return true;
        
        // Check if item is superadmin-only and user is not superadmin
        if (item.superAdminOnly && (!user || user.role !== "superadmin")) {
          return false;
        }
        
        if (!appAccess) return false;
        
        // Check if the item requires edit permissions (for Documents and Prompt Rules)
        if (item.requiresEdit) {
          return appAccess[item.app] && appAccess[item.app].can_edit === true;
        }
        
        // Check primary app permission
        const hasPrimaryAppAccess = appAccess[item.app] && appAccess[item.app].can_view === true;
        
        // Check alternative app permission if defined (e.g., show "Impostazioni" if user has either settings OR ai_schedule)
        const hasAltAppAccess = item.altApp && appAccess[item.altApp] && appAccess[item.altApp].can_view === true;
        
        return hasPrimaryAppAccess || hasAltAppAccess;
      });

  const renderMenuItem = (item, index) => {
    const isActive = location.pathname === item.path;

    // Build item styles with an active indicator for a more "OpenAI-like" look
    const itemSx = {
      minHeight: { xs: 36, sm: 40 },
      justifyContent: isOpen || isMobile ? 'initial' : 'center',
      px: { xs: 1.5, sm: 2 },
      py: { xs: 0.5, sm: 0.75 },
      borderRadius: '10px',
      mx: { xs: 0.5, sm: 1 },
      my: 0.3,
      position: 'relative',
      backgroundColor: isActive ? (darkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(15, 23, 42, 0.06)') : 'transparent',
      color: isActive ? (darkMode ? '#FFFFFF' : '#0f172a') : (darkMode ? '#BBBBBB' : 'inherit'),
      '&:hover': {
        backgroundColor: darkMode ? 'rgba(255, 255, 255, 0.06)' : 'rgba(15, 23, 42, 0.05)',
      },
      transition: 'all 0.25s ease',
    };
    if (isActive) {
      itemSx['&::before'] = {
        content: '""',
        position: 'absolute',
        left: 6,
        top: 8,
        bottom: 8,
        width: 3,
        borderRadius: 2,
        backgroundColor: darkMode ? '#4CC9F0' : '#4361EE',
      };
      itemSx.boxShadow = darkMode
        ? 'inset 0 0 0 1px rgba(255,255,255,0.06)'
        : 'inset 0 0 0 1px rgba(15,23,42,0.06)';
    }

    return (
      <motion.div
        key={item.textKey}
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: index * 0.1 }}
      >
        <Tooltip title={isOpen || isMobile ? '' : t(item.textKey)} placement="right">
          <ListItem
            component={Link}
            to={item.path}
            onClick={isMobile ? handleDrawerToggle : undefined}
            sx={itemSx}
          >
            <ListItemIcon
              sx={{
                minWidth: 0,
                mr: isOpen || isMobile ? { xs: 1.5, sm: 2 } : 'auto',
                justifyContent: 'center',
                color: isActive ? (darkMode ? '#FFFFFF' : '#0f172a') : (darkMode ? '#BBBBBB' : 'inherit'),
              }}
            >
              <item.icon size={18} />
            </ListItemIcon>
            <AnimatePresence>
              {(isOpen || isMobile) && (
                <motion.div
                  initial={{ opacity: 0, width: 0 }}
                  animate={{ opacity: 1, width: 'auto' }}
                  exit={{ opacity: 0, width: 0 }}
                  transition={{ duration: 0.2 }}
                  style={{ overflow: 'hidden', whiteSpace: 'nowrap' }}
                >
                  <ListItemText
                    primary={t(item.textKey)}
                    primaryTypographyProps={{
                      fontSize: 13,
                      fontWeight: isActive ? 600 : 500,
                      noWrap: true,
                    }}
                  />
                </motion.div>
              )}
            </AnimatePresence>
          </ListItem>
        </Tooltip>
      </motion.div>
    );
  };

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {isMobile && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 2 }}>
          <IconButton onClick={handleDrawerToggle} sx={{ color: darkMode ? '#FFFFFF' : 'inherit' }}>
            <X size={24} />
          </IconButton>
        </Box>
      )}
      <List sx={{ flexGrow: 1, px: 2, py: 2 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <CircularProgress />
          </Box>
        ) : (
          filteredMenuItems.map(renderMenuItem)
        )}
      </List>
      <Box 
        sx={{ 
          p: { xs: 1.5, sm: 2 }, 
          mt: 'auto',
          textAlign: 'center',
          borderTop: `1px solid ${darkMode ? 'rgba(255, 255, 255, 0.04)' : 'rgba(15, 23, 42, 0.04)'}`,
        }}
      >
        <AnimatePresence>
          {(isOpen || isMobile) && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              transition={{ duration: 0.2 }}
              style={{ overflow: 'hidden', whiteSpace: 'nowrap' }}
            >
              <Typography 
                variant="caption" 
                noWrap
                sx={{ 
                  color: darkMode ? 'rgba(255, 255, 255, 0.4)' : 'rgba(15, 23, 42, 0.4)', 
                  fontSize: '0.7rem',
                  fontWeight: 500,
                  letterSpacing: '0.5px'
                }}
              >
                © {currentYear} Aitomotivelab
              </Typography>
            </motion.div>
          )}
        </AnimatePresence>
      </Box>
    </Box>
  );

  if (isMobile) {
    return (
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: { xs: '92%', sm: '70%' },
            maxWidth: '300px',
            backgroundColor: darkMode ? '#121212' : '#FFFFFF',
            color: darkMode ? '#FFFFFF' : '#0f172a',
            border: `1px solid ${darkMode ? 'rgba(255,255,255,0.10)' : 'rgba(15,23,42,0.08)'}`,
            borderRadius: '0 16px 16px 0',
            boxShadow: darkMode ? '0 8px 32px rgba(0,0,0,0.6)' : '0 10px 32px rgba(15,23,42,0.12)',
            top: 64,
            height: 'calc(100% - 64px)'
          },
        }}
      >
        {drawer}
      </Drawer>
    );
  }

  return (
    <Drawer
      variant="permanent"
      open={true}
      onMouseEnter={() => handleSidebarOpen(true)}
      onMouseLeave={() => handleSidebarOpen(false)}
      sx={{
        width: isOpen ? 248 : 81,
        flexShrink: 0,
        [`& .MuiDrawer-paper`]: {
          width: isOpen ? 248 : 81,
          boxSizing: 'border-box',
          backgroundColor: darkMode ? 'rgba(17,17,17,0.6)' : 'rgba(255,255,255,0.70)',
          color: darkMode ? '#FFFFFF' : 'inherit',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          border: 'none',
          top: { xs: 72, md: 92 },
          left: 12,
          bottom: 12,
          height: { xs: 'calc(100% - 96px)', md: 'calc(100% - 116px)' },
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
          overflowX: 'hidden',
          boxShadow: darkMode ? '0 4px 24px rgba(0,0,0,0.5)' : '0 6px 24px rgba(15,23,42,0.08)',
          borderRadius: '16px',
        },
      }}
    >
      {drawer}
    </Drawer>
  );
};

export default Sidebar;
